import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, FormControl, InputLabel,
  Select, MenuItem, Grid, Divider, Alert, CircularProgress, Chip,
  Autocomplete, TextField
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

const EXPORT_TYPES = [
  { value: 'campaigns', label: 'Campaigns', filters: [] },
  { value: 'partners', label: 'Partners', filters: [] },
  { value: 'advertisers', label: 'Advertisers', filters: [] },
  { value: 'conversions', label: 'Conversions', filters: ['dateRange', 'campaign', 'partner', 'advertiser'] },
  { value: 'analytics', label: 'Analytics', filters: ['dateRange', 'campaign', 'partner', 'advertiser'] },
  { value: 'fb_leads', label: 'FB Leads', filters: ['dateRange', 'fb_campaign'] },
];

export default function ExportTab() {
  const [exportType, setExportType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [fbCampaigns, setFbCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [selectedFbCampaign, setSelectedFbCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const selected = EXPORT_TYPES.find(t => t.value === exportType);

useEffect(() => {
    axios.get('/admin/campaigns').then(r => {
      const data = r.data;
      setCampaigns(Array.isArray(data) ? data : (data?.campaigns || data?.data || []));
    }).catch(() => {});
    axios.get('/admin/partners').then(r => {
      const data = r.data;
      setPartners(Array.isArray(data) ? data : (data?.partners || data?.data || []));
    }).catch(() => {});
    axios.get('/admin/advertisers').then(r => {
      const data = r.data;
      setAdvertisers(Array.isArray(data) ? data : (data?.advertisers || data?.data || []));
    }).catch(() => {});
    axios.get('/admin/facebook/campaigns').then(r => {
      const data = r.data;
      setFbCampaigns(Array.isArray(data) ? data : (data?.campaigns || data?.data || []));
    }).catch(() => {});
  }, []);

  const handleExport = async () => {
    if (!exportType) return;
    setLoading(true);
    setMessage(null);
    try {
      const params = new URLSearchParams();
      params.append('type', exportType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedCampaign) params.append('campaign_id', selectedCampaign.campaign_id);
      if (selectedPartner) params.append('partner_id', selectedPartner.partner_id);
      if (selectedAdvertiser) params.append('advertiser_id', selectedAdvertiser.advertiser_id);
      if (selectedFbCampaign) params.append('fb_campaign_id', selectedFbCampaign.id);

      const response = await axios.get(`/admin/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${exportType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMessage({ type: 'success', text: 'Export completed successfully!' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Export failed. Please try again.' });
    }
    setLoading(false);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={600} mb={1}>Export Data</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Export your data in CSV format. Select the data type and apply filters as needed.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select value={exportType} label="Data Type" onChange={e => { setExportType(e.target.value); setSelectedCampaign(null); setSelectedPartner(null); setSelectedAdvertiser(null); setSelectedFbCampaign(null); }}>
                {EXPORT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {selected?.filters.includes('dateRange') && (
            <>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Start Date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="End Date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}

          {selected?.filters.includes('campaign') && (
            <Grid item xs={12} md={4}>
              <Autocomplete options={campaigns} getOptionLabel={o => o.name || ''} value={selectedCampaign} onChange={(e, v) => setSelectedCampaign(v)} renderInput={params => <TextField {...params} label="Campaign (optional)" />} />
            </Grid>
          )}

          {selected?.filters.includes('partner') && (
            <Grid item xs={12} md={4}>
              <Autocomplete options={partners} getOptionLabel={o => o.name || ''} value={selectedPartner} onChange={(e, v) => setSelectedPartner(v)} renderInput={params => <TextField {...params} label="Partner (optional)" />} />
            </Grid>
          )}

          {selected?.filters.includes('advertiser') && (
            <Grid item xs={12} md={4}>
              <Autocomplete options={advertisers} getOptionLabel={o => o.name || ''} value={selectedAdvertiser} onChange={(e, v) => setSelectedAdvertiser(v)} renderInput={params => <TextField {...params} label="Advertiser (optional)" />} />
            </Grid>
          )}

          {selected?.filters.includes('fb_campaign') && (
            <Grid item xs={12} md={4}>
              <Autocomplete options={fbCampaigns} getOptionLabel={o => o.campaign_name || ''} value={selectedFbCampaign} onChange={(e, v) => setSelectedFbCampaign(v)} renderInput={params => <TextField {...params} label="FB Campaign (optional)" />} />
            </Grid>
          )}

          {exportType && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} onClick={handleExport} disabled={loading || !exportType} size="large">
                  {loading ? 'Exporting...' : 'Export CSV'}
                </Button>
                {selected && selected.filters.length > 0 && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {selected.filters.map(f => <Chip key={f} label={f} size="small" variant="outlined" />)}
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
